/**
 * Conversation pane for the plugin builder.
 * Displays chat messages, streaming text, verbose events, and guided flow panels.
 */

import { useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '@/lib/utils';
import { useAutoScroll } from '@/lib/hooks/useAutoScroll';
import type { VerboseEvent } from '../types';
import { VerboseTimeline } from '../../../components/VerboseTimeline';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
}

interface PluginConversationPaneProps {
  messages: Message[];
  isProcessing: boolean;
  streamingText: string;
  /** True when the last turn was cut off at the output cap and not recovered. */
  truncated?: boolean;
  /** Invoked when the user clicks "Continue" on the cut-off notice. */
  onContinue?: () => void;
  emptyStateContent?: React.ReactNode;
  headerContent?: React.ReactNode;
  footerContent?: React.ReactNode;
  // Verbose events
  verboseHistory?: Record<string, VerboseEvent[]>;
  pendingVerboseEvents?: VerboseEvent[];
  isAdmin?: boolean;
  // Suggested next steps
  suggestedNextSteps?: string[];
  onSuggestionClick?: (suggestion: string) => void;
  className?: string;
}

/**
 * Three dots that bounce up and down — the "Fias AI is thinking / working"
 * indicator. Uses `bg-current` so it inherits the surrounding text color, and
 * staggered animation delays so the dots ripple. Mirrors the chat arche's
 * TypingIndicator pattern.
 */
function ThinkingDots({ className }: { className?: string }) {
  return (
    <span
      className={cn('inline-flex items-center gap-1', className)}
      role="status"
      aria-label="Fias AI is thinking"
    >
      <span
        className="h-1.5 w-1.5 rounded-full bg-current animate-thinking-bounce"
        style={{ animationDelay: '0ms' }}
      />
      <span
        className="h-1.5 w-1.5 rounded-full bg-current animate-thinking-bounce"
        style={{ animationDelay: '150ms' }}
      />
      <span
        className="h-1.5 w-1.5 rounded-full bg-current animate-thinking-bounce"
        style={{ animationDelay: '300ms' }}
      />
    </span>
  );
}

function MessageBubble({ message, isStreaming }: { message: Message; isStreaming?: boolean }) {
  const isUser = message.role === 'user';

  return (
    <div className={cn('flex w-full', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[85%] rounded-lg px-4 py-3',
          isUser ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground',
        )}
      >
        {isUser ? (
          <p className="whitespace-pre-wrap text-sm">
            {message.content}
            {isStreaming && <ThinkingDots className="ml-1.5 align-middle" />}
          </p>
        ) : (
          <div className="prose prose-sm dark:prose-invert max-w-none break-words text-sm [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
            {isStreaming && <ThinkingDots className="ml-1.5 align-middle" />}
          </div>
        )}
      </div>
    </div>
  );
}

export function PluginConversationPane({
  messages,
  isProcessing,
  streamingText,
  truncated = false,
  onContinue,
  emptyStateContent,
  headerContent,
  footerContent,
  verboseHistory,
  pendingVerboseEvents,
  isAdmin = false,
  suggestedNextSteps,
  onSuggestionClick,
  className,
}: PluginConversationPaneProps) {
  const { containerRef, anchorRef, scrollToBottomIfNeeded } = useAutoScroll();

  useEffect(() => {
    scrollToBottomIfNeeded();
  }, [messages, streamingText, scrollToBottomIfNeeded]);

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {headerContent}
      <div ref={containerRef} className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          emptyStateContent ? (
            emptyStateContent
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-sm text-muted-foreground">
                Describe what you want to build below.
              </p>
            </div>
          )
        ) : (
          messages.map((message, index) => {
            // Show verbose events after each assistant message (if admin)
            const messageKey = message.timestamp || String(index);
            const events = isAdmin && verboseHistory ? verboseHistory[messageKey] || [] : [];

            return (
              <div key={index}>
                <MessageBubble message={message} />
                {events.length > 0 && <VerboseTimeline events={events} />}
              </div>
            );
          })
        )}

        {/* Streaming message */}
        {isProcessing &&
          (streamingText ? (
            <div>
              <MessageBubble message={{ role: 'assistant', content: streamingText }} isStreaming />
              {isAdmin && pendingVerboseEvents && pendingVerboseEvents.length > 0 && (
                <VerboseTimeline events={pendingVerboseEvents} isPending />
              )}
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <ThinkingDots />
                <span className="text-sm">Fias AI is thinking...</span>
              </div>
              {isAdmin && pendingVerboseEvents && pendingVerboseEvents.length > 0 && (
                <VerboseTimeline events={pendingVerboseEvents} isPending />
              )}
            </div>
          ))}

        {/* Cut-off notice — the last turn ended early (output-token cap or
            tool-iteration cap) before its work completed. Offer a one-click
            Continue rather than leaving the user staring at an unfinished
            response. */}
        {!isProcessing && truncated && (
          <div
            role="status"
            className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-700/60 dark:bg-amber-950/40 dark:text-amber-200"
          >
            <p>The response ended before Fias AI finished working.</p>
            {onContinue && (
              <button
                type="button"
                onClick={onContinue}
                className="mt-2 rounded-md border border-amber-400 bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted transition-colors"
              >
                Continue
              </button>
            )}
          </div>
        )}

        {/* Suggested next steps */}
        {!isProcessing &&
          suggestedNextSteps &&
          suggestedNextSteps.length > 0 &&
          onSuggestionClick && (
            <div className="flex flex-wrap gap-2 pt-2">
              {suggestedNextSteps.map((step, i) => (
                <button
                  key={i}
                  onClick={() => onSuggestionClick(step)}
                  className="px-3 py-1.5 text-xs rounded-full border bg-background hover:bg-muted transition-colors"
                >
                  {step}
                </button>
              ))}
            </div>
          )}
        <div ref={anchorRef} />
      </div>
      {footerContent}
    </div>
  );
}
